// The desk sync engine: TanStack Query's native paused-mutation queue,
// persisted (allow-listed) into the encrypted vault, plus the
// Needs-attention tray and a short local log of what this desk did.
//
// Not a bespoke queue (plan "Offline"): every check-in / balance entry is a
// TanStack mutation keyed by DESK_MUTATION_KEYS, whose mutationFn comes from
// setMutationDefaults so a mutation restored from IndexedDB after a reload
// knows how to run. Pending mutations are persisted as paused, so they
// resume on the next boot even if the tab died mid-request (the server's
// clientEventId idempotency makes that replay safe).
//
// Nothing is dropped silently: an event leaves the queue only as `done`
// (applied / already_checked_in) or by landing in the Needs-attention tray,
// and the tray entry is written BEFORE the mutation leaves `pending`
// (Mutation.execute awaits onError before dispatching the error state).

import { dehydrate, hydrate, type DehydratedState, type Mutation, type QueryClient } from '@tanstack/react-query';
import {
  DESK_MUTATION_KEYS,
  deviceLabel,
  type DeskEvent,
} from './events';
import {
  DeskSyncError,
  classifyResponse,
  shouldRetrySync,
  syncRetryDelay,
  type AttentionCategory,
  type DoneOutcome,
  type RetryReason,
} from './outcomes';
import { deleteDeskDb } from './idb';
import type { DeskInvoke } from './transport';
import { DeskVault } from './vault';
import { cancelThenInvalidate } from '@/lib/query/refresh';

/** Query keys (first segment) allowed into the persisted cache. Everything
 * else stays memory-only. `portal-session` (memberships: property ids,
 * names, roles — no guest data) lets the shell boot offline;
 * `desk-manifest` is the offline verification list. */
export const PERSISTED_QUERY_KEYS: ReadonlySet<string> = new Set(['portal-session', 'desk-manifest']);

export interface AttentionItem {
  id: string;
  event: DeskEvent;
  category: AttentionCategory | 'parked';
  reason: string;
  message: string;
  status: number;
  at: string;
}

export interface DeskLogEntry {
  event: DeskEvent;
  /** `unsaved`: captured and in memory, but the write to this device's
   * storage failed — it is lost if the tab closes before it syncs. */
  state: 'queued' | 'unsaved' | 'synced' | 'attention';
  outcome?: DoneOutcome;
  syncedAt?: string;
  /** Server response fields worth showing (checkedInAt, balance, eventId). */
  result?: Record<string, unknown>;
}

export type DeskBlocker =
  | { reason: 'signed_out' | 'other_owner' | 'version_mismatch' | 'desk_key_revoked'; events: DeskEvent[] }
  | null;

export interface DeskSnapshot {
  ready: boolean;
  /** False when another tab of this browser holds the desk. */
  leader: boolean;
  pending: DeskEvent[];
  attention: AttentionItem[];
  log: DeskLogEntry[];
  blocker: DeskBlocker;
  /** Bumped every time the queue drains to zero with no new attention
   * items — the sync bar's 4-second success flash keys off it. */
  drainedAt: number | null;
}

interface PersistedDeskClient {
  timestamp: number;
  clientState: DehydratedState;
}

const LOG_LIMIT = 100;
const LOG_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const PARKED_MESSAGES: Record<RetryReason, string> = {
  network: 'Could not reach NamibiaGo after 8 attempts. Check the connection, then retry.',
  rate_limited: 'NamibiaGo is limiting lookups from this account. Wait a minute, then retry.',
  server_error: 'NamibiaGo had a problem saving this after 8 attempts. Retry, or record it on the booking later.',
};

/** The desk log keeps every entry that isn't synced yet (queued, unsaved,
 * needs attention) no matter how old, and synced entries for 48 h after they
 * SYNCED — not after capture: an event captured offline days ago that just
 * reached NamibiaGo must still show as synced. Only synced entries count
 * against the 100-entry cap (oldest dropped first). Exported for tests. */
export function pruneLog(entries: DeskLogEntry[], now: Date): DeskLogEntry[] {
  const cutoff = now.getTime() - LOG_MAX_AGE_MS;
  const kept = entries.filter(
    (e) => e.state !== 'synced' || Date.parse(e.syncedAt ?? e.event.capturedAt) >= cutoff,
  );
  let excess = kept.length - LOG_LIMIT;
  if (excess <= 0) return kept;
  const result: DeskLogEntry[] = [];
  for (let i = kept.length - 1; i >= 0; i -= 1) {
    const entry = kept[i]!;
    if (excess > 0 && entry.state === 'synced') {
      excess -= 1;
      continue;
    }
    result.unshift(entry);
  }
  return result;
}

function uniqueAttention(items: AttentionItem[]): AttentionItem[] {
  // Latest wins per clientEventId.
  const byId = new Map<string, AttentionItem>();
  for (const item of items) byId.set(item.id, item);
  return [...byId.values()];
}

function uniqueEvents(events: DeskEvent[]): DeskEvent[] {
  const seen = new Set<string>();
  return events.filter((e) => !seen.has(e.clientEventId) && seen.add(e.clientEventId));
}

function isDeskMutation(mutation: Pick<Mutation, 'options'>): boolean {
  const key = mutation.options.mutationKey;
  return Array.isArray(key) && key[0] === 'desk';
}

export function requestBody(event: DeskEvent): Record<string, unknown> {
  const device = deviceLabel(event);
  switch (event.kind) {
    case 'check_in':
      return event.token
        ? { action: 'redeem', token: event.token, clientEventId: event.clientEventId, device }
        : {
            action: 'redeem',
            reference: event.reference,
            surname: event.surname ?? undefined,
            clientEventId: event.clientEventId,
            device,
          };
    case 'balance_record':
      return {
        action: 'record',
        bookingId: event.bookingId,
        clientEventId: event.clientEventId,
        amountNad: event.amountNad,
        method: event.method,
        device,
      };
    case 'balance_undo':
      return {
        action: 'undo',
        bookingId: event.bookingId,
        clientEventId: event.clientEventId,
        undoClientEventId: event.undoClientEventId,
        device,
      };
  }
}

/** Dehydrates only allow-listed queries and every pending desk mutation,
 * marking the mutations paused so they resume after a reload. */
export function dehydrateDesk(queryClient: QueryClient): DehydratedState {
  const state = dehydrate(queryClient, {
    shouldDehydrateQuery: (query) =>
      query.state.status === 'success' && PERSISTED_QUERY_KEYS.has(String(query.queryKey[0])),
    shouldDehydrateMutation: (mutation) => isDeskMutation(mutation) && mutation.state.status === 'pending',
  });
  return {
    ...state,
    mutations: state.mutations.map((m) => ({ ...m, state: { ...m.state, isPaused: true } })),
  };
}

/** Events held in a persisted client blob — used to count what a wipe
 * would lose before the engine has hydrated anything. */
function pendingEventsIn(persisted: PersistedDeskClient | undefined): DeskEvent[] {
  return (persisted?.clientState.mutations ?? [])
    .filter((m) => Array.isArray(m.mutationKey) && m.mutationKey[0] === 'desk' && m.state.status === 'pending')
    .map((m) => m.state.variables as DeskEvent);
}

export interface DeskEngineHooks {
  /** A 401 `desk_key_revoked` arrived — the device's desk access was
   * revoked; local state must be wiped (behind the unsynced modal). */
  onKeyRevoked?: () => void;
  /** Called after a wipe so other tabs can follow. */
  onWiped?: () => void;
  now?: () => Date;
  /** Injectable for tests; defaults to syncRetryDelay. */
  retryDelay?: (failureCount: number, error: unknown) => number;
  /** Injectable for tests; defaults to DeskVault.open. */
  openVault?: (userId: string) => Promise<DeskVault>;
}

export class DeskEngine {
  private vault: DeskVault | null = null;
  private listeners = new Set<() => void>();
  private persistUnsubscribers: Array<() => void> = [];
  private activeUserId: string | null = null;
  // Queued events only talk to the server while the vault's owner is the
  // signed-in user and nothing is blocking — otherwise a paused queue could
  // resume under a different account's session. A closed gate makes the
  // mutationFn wait (the event stays pending); it never fails it.
  private gate!: Promise<void>;
  private openGateFn: (() => void) | null = null;
  private attention: AttentionItem[] = [];
  private log: DeskLogEntry[] = [];
  private qrSeen: Record<string, number> = {};
  private blocker: DeskBlocker = null;
  private leader = true;
  private drainedAt: number | null = null;
  private lastPendingCount = 0;
  private lastPendingKey = '';
  private snapshot: DeskSnapshot;
  // Serialised, latest-wins writer for the persisted client.
  private writing: Promise<void> | null = null;
  private dirty = false;
  private persistWaiters: Array<{ resolve: () => void; reject: (error: unknown) => void }> = [];
  // Attention writes run one at a time and each computes its value when it
  // RUNS, so a slow write can never overwrite a newer tray state.
  private attentionWrites: Promise<void> = Promise.resolve();

  constructor(
    readonly queryClient: QueryClient,
    private readonly invoke: DeskInvoke,
    private readonly hooks: DeskEngineHooks = {},
  ) {
    this.closeGate();
    this.registerMutationDefaults();
    this.snapshot = this.buildSnapshot();
    queryClient.getMutationCache().subscribe(() => {
      this.onMutationCacheChange();
    });
  }

  /** Signed in as `userId` (boot, or a later sign-in). Opens the vault for
   * that user, hydrates and resumes the queue — or raises a blocker when the
   * device holds another account's (or an older build's) unsynced events. */
  start(userId: string): Promise<void> {
    return this.serial(() => this.startNow(userId));
  }

  private async startNow(userId: string): Promise<void> {
    this.activeUserId = userId;
    // Only the tab holding the desk lock owns the persisted queue; a follower
    // tab starts when (if) it inherits the lock — see DeskProvider.
    if (!this.leader) return;
    if (this.vault && !this.vault.conflict) {
      if (this.vault.ownerId === userId) {
        this.resumeAfterSignIn();
      } else {
        this.blocker = { reason: 'other_owner', events: this.unsyncedEvents() };
        this.emit();
      }
      return;
    }
    if (this.vault) {
      // A vault that was held back by a conflict: re-evaluate it for the
      // account now signed in (e.g. its owner signed back in).
      this.detach();
      this.blocker = null;
    }
    const open = this.hooks.openVault ?? DeskVault.open;
    if ((await this.attach(await open(userId))) === 'wiped') await this.attach(await open(userId));
  }

  /** "Discard" on the unsynced modal: wipe, then carry on as the signed-in
   * user (if any). */
  discardAndRestart(): Promise<void> {
    return this.serial(async () => {
      const userId = this.activeUserId;
      await this.wipeNow();
      if (userId) await this.startNow(userId);
    });
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /** Hydrates the persisted queue from `vault`, starts persisting, and
   * resumes paused events. A vault with a conflict (other owner / version
   * mismatch) is NOT hydrated: if it holds unsynced events the engine
   * reports a blocker; otherwise it is wiped and the caller reopens. */
  async attach(vault: DeskVault): Promise<'attached' | 'blocked' | 'wiped'> {
    if (vault.conflict) {
      const events = await DeskEngine.unsyncedEventsIn(vault);
      if (events.length === 0) {
        await vault.destroy();
        return 'wiped';
      }
      this.vault = vault;
      this.blocker = { reason: vault.conflict, events };
      this.emit();
      return 'blocked';
    }

    let persisted: PersistedDeskClient | undefined;
    try {
      persisted = await vault.read<PersistedDeskClient>('query-client');
      this.attention = uniqueAttention((await vault.read<AttentionItem[]>('attention')) ?? []);
      this.log = (await vault.read<DeskLogEntry[]>('desk-log')) ?? [];
      this.qrSeen = (await vault.read<Record<string, number>>('qr-seen')) ?? {};
    } catch (error) {
      // A record that fails AES-GCM authentication was tampered with (moved
      // between slots, meta.owner/version rewritten) — it can never be
      // decrypted, by us or anyone. Start the device over.
      console.error('desk: local store failed authentication — resetting it', error);
      this.attention = [];
      this.log = [];
      this.qrSeen = {};
      await vault.destroy();
      return 'wiped';
    }
    this.vault = vault;
    if (persisted) hydrate(this.queryClient, persisted.clientState);

    const persist = () => {
      // Background saves: a failure is logged by schedulePersist; the event
      // that needed the save is told via its own enqueue().
      this.schedulePersist().catch(() => undefined);
    };
    this.persistUnsubscribers.push(this.queryClient.getQueryCache().subscribe(persist));
    this.persistUnsubscribers.push(this.queryClient.getMutationCache().subscribe(persist));
    this.openGate();
    this.emit();
    await this.queryClient.resumePausedMutations();
    return 'attached';
  }

  static async unsyncedEventsIn(vault: DeskVault): Promise<DeskEvent[]> {
    let pending: DeskEvent[] = [];
    let attention: AttentionItem[] = [];
    try {
      pending = pendingEventsIn(await vault.read<PersistedDeskClient>('query-client'));
    } catch {
      /* unreadable blob — nothing we can replay */
    }
    try {
      attention = (await vault.read<AttentionItem[]>('attention')) ?? [];
    } catch {
      /* same */
    }
    return uniqueEvents([...pending, ...attention.map((a) => a.event)]);
  }

  /** Leadership (the desk lock) changed. Gaining it starts the engine for
   * the signed-in user, if any. */
  setLeader(leader: boolean): Promise<void> {
    return this.serial(async () => {
      this.leader = leader;
      this.emit();
      if (leader && this.activeUserId && !this.vault) await this.startNow(this.activeUserId);
    });
  }

  /** Another tab says it wiped the device: forget everything in memory
   * too. Ignored by a tab that holds the vault or unsynced events — only
   * the vault holder can delete the database, so the message can't be about
   * data this tab still has (and must never make it drop events). */
  handleRemoteWipe(): void {
    if (this.vault || this.unsyncedEvents().length > 0) return;
    this.detach();
    this.queryClient.clear();
    this.attention = [];
    this.log = [];
    this.qrSeen = {};
    this.blocker = null;
    this.emit();
  }

  /** Stops persisting (the vault stays as it is). */
  detach(): void {
    this.closeGate();
    for (const unsubscribe of this.persistUnsubscribers.splice(0)) unsubscribe();
    this.vault?.close();
    this.vault = null;
  }

  /** Every event not yet confirmed by the server. */
  unsyncedEvents(): DeskEvent[] {
    if (this.blocker) return this.blocker.events;
    return uniqueEvents([...this.pendingEvents(), ...this.attention.map((a) => a.event)]);
  }

  /** Unsynced events on this DEVICE — for a follower tab this reads the
   * desk tab's vault read-only, so its sign-out is gated too. */
  async unsyncedEventsOnDevice(): Promise<DeskEvent[]> {
    if (this.vault || this.blocker) return this.unsyncedEvents();
    const peeked = await DeskVault.peek();
    if (!peeked) return this.unsyncedEvents();
    try {
      return uniqueEvents([...this.unsyncedEvents(), ...(await DeskEngine.unsyncedEventsIn(peeked))]);
    } finally {
      peeked.close();
    }
  }

  /** wipeLocalState(): forgets everything this browser holds for the desk —
   * the query cache (memory), the paused queue, the tray, the log, and the
   * IndexedDB database with its key. Callers gate this behind the unsynced
   * modal; this method itself does not ask. */
  wipe(): Promise<void> {
    return this.serial(() => this.wipeNow());
  }

  private async wipeNow(): Promise<void> {
    const vault = this.vault;
    this.detach();
    this.queryClient.clear();
    this.attention = [];
    this.log = [];
    this.qrSeen = {};
    this.blocker = null;
    this.drainedAt = null;
    this.lastPendingCount = 0;
    this.lastPendingKey = '';
    let destroyed = false;
    if (vault) {
      await vault.destroy();
      destroyed = true;
    } else if (this.leader) {
      // A follower tab never deletes the leader's database from under it.
      await deleteDeskDb();
      destroyed = true;
    }
    this.emit();
    // Only a tab that actually deleted the database tells the others.
    if (destroyed) this.hooks.onWiped?.();
  }

  /** Signed out (this tab, another tab, or the session died). Guest data
   * leaves memory at once; unsynced events are kept (vault untouched) and
   * surfaced as a blocker so the receptionist can sign back in and sync, or
   * explicitly download/discard them. With nothing unsynced, wipe. */
  handleSignedOut(): Promise<void> {
    // Guest data leaves memory at once, before anything async (Task 10's
    // guarantee for the query cache); the queue is decided below.
    this.queryClient.getQueryCache().clear();
    return this.serial(() => this.handleSignedOutNow());
  }

  private async handleSignedOutNow(): Promise<void> {
    this.activeUserId = null;
    this.closeGate();
    const events = this.unsyncedEvents();
    if (events.length === 0) {
      await this.wipeNow();
      return;
    }
    this.queryClient.getQueryCache().clear();
    this.blocker = { reason: 'signed_out', events };
    this.emit();
  }

  /** The owner signed back in: lift the signed-out blocker and resume. */
  resumeAfterSignIn(): void {
    if (this.blocker?.reason === 'signed_out') this.blocker = null;
    if (!this.blocker && this.vault) {
      this.openGate();
      this.emit();
      void this.queryClient.resumePausedMutations();
    }
  }

  requestKeyRevokedWipe(): void {
    const events = this.unsyncedEvents();
    if (events.length === 0) {
      void this.wipe();
      return;
    }
    this.closeGate();
    this.blocker = { reason: 'desk_key_revoked', events };
    this.emit();
  }

  // Lifecycle operations (start, sign-out, wipe, leadership) run one at a
  // time: a sign-out's wipe must finish before the next sign-in opens the
  // database again.
  private lifecycle: Promise<void> = Promise.resolve();

  private serial(operation: () => Promise<void>): Promise<void> {
    const next = this.lifecycle.then(operation);
    this.lifecycle = next.catch(() => undefined);
    return next;
  }

  private closeGate(): void {
    if (this.openGateFn) return;
    this.gate = new Promise<void>((resolve) => {
      this.openGateFn = resolve;
    });
  }

  private openGate(): void {
    const open = this.openGateFn;
    this.openGateFn = null;
    open?.();
  }

  // ── Queue ─────────────────────────────────────────────────────────────────

  /** Queues a desk event. Resolves only once the event is durably in the
   * persisted queue (and the desk log); the sync runs in the background. */
  async enqueue(event: DeskEvent): Promise<void> {
    if (!this.vault || this.blocker) throw new Error('desk is not ready — the event was not recorded');
    this.upsertLog({ event, state: 'queued' });
    const mutation = this.queryClient.getMutationCache().build(this.queryClient, {
      mutationKey: DESK_MUTATION_KEYS[event.kind] as unknown as string[],
      scope: { id: `booking:${event.bookingId}` },
    });
    // execute() marks the mutation `pending` synchronously, so the persist
    // below includes it. Errors are handled in onError (tray).
    void mutation.execute(event).catch(() => undefined);
    try {
      await this.persistQueueNow();
    } catch (error) {
      // The event still exists in memory (and still syncs if it can), but
      // it is NOT on disk: say so in the log rather than leave it "queued",
      // and let the caller surface it.
      this.upsertLog({ event, state: 'unsaved' });
      throw error;
    }
    await this.persistSide('desk-log');
  }

  /** Re-queues a tray item. The item leaves the tray only after the new
   * queue entry is persisted — a crash in between leaves it in both places
   * (harmless: the clientEventId makes the replay idempotent), never in
   * neither. */
  async retryAttention(id: string): Promise<void> {
    const item = this.attention.find((a) => a.id === id);
    if (!item) return;
    await this.enqueue(item.event);
    // Identity, not id: if the retry already failed again, onFailed put a
    // NEW item for this id in the tray, which must stay. The write computes
    // the tray from the CURRENT state when it runs; memory changes only after
    // the disk write succeeded.
    await this.writeAttention(item);
    this.attention = this.attention.filter((a) => a !== item);
    this.emit();
  }

  /** The receptionist dealt with it (explicit action — never automatic). */
  async resolveAttention(id: string): Promise<void> {
    this.attention = this.attention.filter((a) => a.id !== id);
    await this.persistSide('attention');
    this.emit();
  }

  // ── QR version memory (plan: reject a lower qr_version than one seen) ─────

  lastSeenQrVersion(bookingId: string): number | undefined {
    return this.qrSeen[bookingId];
  }

  async rememberQrVersion(bookingId: string, version: number): Promise<void> {
    if ((this.qrSeen[bookingId] ?? 0) >= version) return;
    this.qrSeen = { ...this.qrSeen, [bookingId]: version };
    await this.persistSide('qr-seen');
  }

  // ── React binding ─────────────────────────────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DeskSnapshot => this.snapshot;

  /** Resolves once every queued write to IndexedDB has landed (tests). */
  async flush(): Promise<void> {
    while (this.writing) await this.writing;
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private pendingEvents(): DeskEvent[] {
    return this.queryClient
      .getMutationCache()
      .getAll()
      .filter((m) => isDeskMutation(m) && m.state.status === 'pending')
      .map((m) => m.state.variables as DeskEvent);
  }

  private buildSnapshot(): DeskSnapshot {
    return {
      ready: this.vault !== null,
      leader: this.leader,
      pending: this.pendingEvents(),
      attention: this.attention,
      log: this.log,
      blocker: this.blocker,
      drainedAt: this.drainedAt,
    };
  }

  private emit(): void {
    this.snapshot = this.buildSnapshot();
    for (const listener of this.listeners) listener();
  }

  private onMutationCacheChange(): void {
    // The mutation cache also notifies for things like observers being
    // created during another component's render (any useMutation); only
    // emit when the desk queue actually changed, so subscribers (the sync
    // bar) never get a setState in the middle of someone else's render.
    const pending = this.pendingEvents();
    const key = pending.map((e) => e.clientEventId).join(',');
    if (key === this.lastPendingKey) return;
    this.lastPendingKey = key;
    if (this.lastPendingCount > 0 && pending.length === 0) this.drainedAt = Date.now();
    this.lastPendingCount = pending.length;
    this.emit();
  }

  private registerMutationDefaults(): void {
    const functionFor = (kind: DeskEvent['kind']) => (kind === 'check_in' ? 'redeem-booking' : 'host-balance');
    for (const kind of Object.keys(DESK_MUTATION_KEYS) as Array<DeskEvent['kind']>) {
      this.queryClient.setMutationDefaults(DESK_MUTATION_KEYS[kind] as unknown as string[], {
        networkMode: 'online',
        retry: shouldRetrySync,
        retryDelay: this.hooks.retryDelay ?? syncRetryDelay,
        mutationFn: async (variables: unknown) => {
          const event = variables as DeskEvent;
          await this.gate;
          const classified = classifyResponse(await this.invoke(functionFor(event.kind), requestBody(event)));
          if (classified.kind === 'done') return { outcome: classified.outcome, body: classified.body };
          throw new DeskSyncError(classified);
        },
        onSuccess: async (data: unknown, variables: unknown) => {
          await this.onSynced(variables as DeskEvent, data as { outcome: DoneOutcome; body: Record<string, unknown> });
        },
        onError: async (error: unknown, variables: unknown) => {
          await this.onFailed(variables as DeskEvent, error);
        },
      });
    }
  }

  private async onSynced(event: DeskEvent, data: { outcome: DoneOutcome; body: Record<string, unknown> }) {
    const booking = (data.body.booking ?? null) as Record<string, unknown> | null;
    this.upsertLog({
      event,
      state: 'synced',
      outcome: data.outcome,
      syncedAt: this.now().toISOString(),
      result: {
        checkedInAt: booking?.checkedInAt ?? null,
        balance: data.body.balance ?? null,
        eventId: data.body.eventId ?? null,
      },
    });
    await this.persistSide('desk-log');
    void cancelThenInvalidate(this.queryClient, {
      predicate: (q) =>
        ['bookings', 'booking', 'booking-events', 'bookings-range', 'desk-manifest'].includes(String(q.queryKey[0])),
    });
  }

  private async onFailed(event: DeskEvent, error: unknown) {
    const classified =
      error instanceof DeskSyncError
        ? error.classified
        : ({ kind: 'retry', reason: 'network', status: 0 } as const);
    const item: AttentionItem =
      classified.kind === 'attention'
        ? {
            id: event.clientEventId,
            event,
            category: classified.category,
            reason: classified.reason,
            message: classified.message,
            status: classified.status,
            at: this.now().toISOString(),
          }
        : {
            id: event.clientEventId,
            event,
            category: 'parked',
            reason: classified.reason,
            message: PARKED_MESSAGES[classified.reason],
            status: classified.status,
            at: this.now().toISOString(),
          };
    this.attention = [...this.attention.filter((a) => a.id !== item.id), item];
    this.upsertLog({ event, state: 'attention' });
    await this.persistSide('attention');
    await this.persistSide('desk-log');
    this.emit();
    if (classified.kind === 'attention' && classified.reason === 'desk_key_revoked') this.hooks.onKeyRevoked?.();
  }

  private now(): Date {
    return this.hooks.now?.() ?? new Date();
  }

  private upsertLog(entry: DeskLogEntry): void {
    this.log = pruneLog([entry, ...this.log.filter((e) => e.event.clientEventId !== entry.event.clientEventId)], this.now());
    this.emit();
  }

  private async persistSide(name: 'attention' | 'desk-log' | 'qr-seen'): Promise<void> {
    if (name === 'attention') return this.writeAttention();
    if (!this.vault) return;
    await this.vault.write(name, name === 'desk-log' ? this.log : this.qrSeen);
  }

  /** Serialized tray write; the value is taken from the current state at
   * write time (minus `excluding`), de-duplicated by clientEventId. */
  private writeAttention(excluding?: AttentionItem): Promise<void> {
    const run = async () => {
      if (!this.vault) return;
      await this.vault.write('attention', uniqueAttention(this.attention.filter((a) => a !== excluding)));
    };
    const next = this.attentionWrites.then(run, run);
    this.attentionWrites = next.catch(() => undefined);
    return next;
  }

  /** An event whose own save failed is on disk after all once a later
   * queue write includes it: flip its log entry back from `unsaved`. */
  private markSaved(snapshot: PersistedDeskClient): void {
    const saved = new Set(pendingEventsIn(snapshot).map((e) => e.clientEventId));
    let changed = false;
    this.log = this.log.map((entry) => {
      if (entry.state !== 'unsaved' || !saved.has(entry.event.clientEventId)) return entry;
      changed = true;
      return { ...entry, state: 'queued' };
    });
    if (changed) {
      this.emit();
      void this.persistSide('desk-log').catch(() => undefined);
    }
  }

  /** Writes the queue now; resolves when a write that includes the current
   * state is on disk, and rejects with THAT write's error. */
  private persistQueueNow(): Promise<void> {
    return this.schedulePersist();
  }

  /** Coalescing, serialized writer for the persisted queue. Each call gets
   * the outcome of the first write that starts after it (so the write
   * includes the caller's state). */
  private schedulePersist(): Promise<void> {
    if (!this.vault) return Promise.resolve();
    const done = new Promise<void>((resolve, reject) => this.persistWaiters.push({ resolve, reject }));
    this.dirty = true;
    if (!this.writing) {
      this.writing = (async () => {
        try {
          while (this.dirty && this.vault) {
            this.dirty = false;
            const batch = this.persistWaiters.splice(0);
            const vault = this.vault;
            const snapshot: PersistedDeskClient = { timestamp: Date.now(), clientState: dehydrateDesk(this.queryClient) };
            try {
              await vault.write('query-client', snapshot);
              for (const waiter of batch) waiter.resolve();
              this.markSaved(snapshot);
            } catch (error) {
              if (this.vault !== vault) {
                // A write racing a detach/wipe of this very vault is moot.
                for (const waiter of batch) waiter.resolve();
              } else {
                console.error('desk: failed to persist the offline queue', error);
                for (const waiter of batch) waiter.reject(error);
              }
            }
          }
        } finally {
          this.writing = null;
          // Detached while waiting: nothing left to write to.
          for (const waiter of this.persistWaiters.splice(0)) waiter.resolve();
        }
      })();
    }
    return done;
  }
}
